import React, { useMemo } from 'react'
import { calculateAge } from '@/util/person.ts'
import TruncatedText from '@/component/tooltip/TruncatedText.tsx'
import AlertStatusBadge from '@/component/badge/AlertStatusBadge.tsx'
import BatteryStatusBadge from '@/component/badge/BatteryStatusBadge.tsx'
import { DeviceStatus } from '@/api/user/getUsers.ts'
import { IUserStatusListItem } from '@/api/user/getUsersStatusList.ts'
import { useTranslation } from 'react-i18next'
import useQueryGetMe from '@/api/auth/getMe.ts'

export interface IProps extends IUserStatusListItem {
    className?: string
    isSelected?: boolean
    onClick: (userId: number) => void
} 

const SearchUserItem: React.FC<IProps> = ({
    className,
    id,
    alertStatus,
    name,
    birthDate,
    gender,
    device,
    isSelected = false,
    onClick
}) => {
    const { t } = useTranslation()
    const { data: me } = useQueryGetMe()

    // 특정 이메일 계정인지 확인
    const isMaskingRequired = useMemo(() => {
        return me?.email === 'dbd_cm@clomental.com'
    }, [me?.email])

    // 마스킹 처리 함수
    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }

    const genderName = useMemo(() => {
        if (gender == 0) return t('users.list.item.gender.male')
        if (gender == 1) return t('users.list.item.gender.female')
        else return t('users.list.item.gender.none')
    }, [gender, t])

    const currentBattery = device?.battery ?? undefined
    const actualDeviceStatus = device?.status

    const statusTone = useMemo(() => {
        if (!device) return 'is-disconnected'
        if (actualDeviceStatus === DeviceStatus.EMERGENCY) return 'is-emergency'
        if (actualDeviceStatus === DeviceStatus.CHARGING) return 'is-charging'
        if (actualDeviceStatus === DeviceStatus.OFFLINE) return 'is-offline'
        if (actualDeviceStatus === DeviceStatus.ONLINE && currentBattery !== undefined && currentBattery < 15) return 'is-low-battery'
        return 'is-online'
    }, [actualDeviceStatus, currentBattery, device])

    const statusText = useMemo(() => {
        if (!device) return t('users.list.item.device.disconnected')
        if (actualDeviceStatus === DeviceStatus.EMERGENCY) return t('users.list.item.device.status.emergency')
        if (actualDeviceStatus === DeviceStatus.CHARGING) return t('users.list.item.device.status.charging')
        if (actualDeviceStatus === DeviceStatus.OFFLINE) return t('users.list.item.device.status.offline')
        if (actualDeviceStatus === DeviceStatus.ONLINE && currentBattery !== undefined && currentBattery < 15) return t('users.list.item.device.status.lowBattery')
        return t('users.list.item.device.status.normal')
    }, [actualDeviceStatus, currentBattery, device, t])

    return (
        <>
            <button
                type="button"
                className={`${className || ''} user-list-card ${statusTone} ${isSelected ? 'is-selected' : ''}`}
                aria-pressed={isSelected}
                onClick={() => onClick(id)}
            >
                <span className="user-list-card__avatar">{getMaskedName(name)?.trim().charAt(0) || '?'}</span>
                <span className="user-list-card__content">
                    <span className="user-list-card__name-row">
                        <TruncatedText className="user-list-card__name" text={getMaskedName(name)} />
                        <AlertStatusBadge value={alertStatus} className="h-[14px] w-[14px]" />
                    </span>
                    <span className="user-list-card__meta">
                        {t('users.list.item.age', { count: calculateAge(birthDate ?? undefined) })} · {genderName}
                    </span>
                    <span className="user-list-card__status">
                        <i />
                        {statusText}
                    </span>
                </span>
                <span className="user-list-card__device">
                    {device ? (
                        <BatteryStatusBadge
                            battery={currentBattery}
                            status={actualDeviceStatus}
                            showPercentage={true}
                            className="text-xs"
                        />
                    ) : (
                        <span className="user-list-card__no-device">{t('users.list.item.device.disconnected')}</span>
                    )}
                </span>
                <span className="user-list-card__arrow" aria-hidden>›</span>
            </button>
        </>
    )
}

export default SearchUserItem
